import { motion } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';
import { Layers, Sparkles, Check, BarChart3, TrendingUp, AlertCircle, FlaskConical } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useEngine } from '../hooks/useEngine.js';
import { PoolsView } from '../components/PoolGrid.jsx';
import { run as runFormulas, buildKillPool, buildDanPool, buildSmartKillPool, BACKTEST } from '../lib/formulas.js';
import { backtestPools } from '../lib/poolGenerator.js';
import { useHistoryStore } from '../stores/history.js';

export default function PoolPage() {
  const [userKills, setUserKills] = useState([]);
  const [backtest, setBacktest] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const updatedAt = useHistoryStore(s => s.updatedAt);

  const toggleKill = (n) => {
    setUserKills(prev =>
      prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]
    );
  };

  // 把 userKills 传给 useEngine,池子会跟着过滤
  const { pools, result, killPool, danPool, smartKill, heatMap, history } = useEngine(userKills);

  // 跑 5 折 CV 回测(MAX v5)
  useEffect(() => {
    if (!history || history.length < 10) return;
    setBacktestLoading(true);
    backtestPools(history, runFormulas, buildKillPool, buildDanPool, buildSmartKillPool, 30)
      .then(r => setBacktest(r))
      .catch(e => console.warn('[backtest] failed:', e.message))
      .finally(() => setBacktestLoading(false));
  }, [history[0]?.p, history.length]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Hero */}
      <div className="card relative overflow-hidden p-4 sm:p-5">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-brand-purple/30 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-brand-cyan/20 blur-3xl"></div>
        </div>
        <div className="relative flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl gradient-bg flex items-center justify-center flex-shrink-0">
            <Layers size={20} className="text-bg-base sm:hidden" />
            <Layers size={28} className="text-bg-base hidden sm:block" />
          </div>
          <div className="flex-1 min-w-[150px]">
            <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
              4 层大底池
              <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-md bg-brand-rose/20 text-brand-rose border border-brand-rose/30 font-bold">
                MAX
              </span>
            </h1>
            <p className="text-[10px] sm:text-sm text-ink-3 mt-0.5 sm:mt-1">
              层层筛选 1000 / 500 / 300 / 100 注大底池
              {updatedAt && (
                <span className="text-ink-3 ml-1.5">
                  · 同步 {Math.max(0, Math.floor((Date.now() - updatedAt) / 60000))} 分钟前
                </span>
              )}
            </p>
          </div>
          <button
            onClick={async () => {
              const text = [
                `[L1 1000 注基础池] ${pools.pool1000.map(x => x.code).join(' ')}`,
                `[L2 500 注] ${pools.pool500.map(x => x.code).join(' ')}`,
                `[L3 300 注] ${pools.pool300.map(x => x.code).join(' ')}`,
                `[L4 100 注] ${pools.pool100.map(x => x.code).join(' ')}`,
              ].join('\n\n');
              await navigator.clipboard.writeText(text);
              toast.success('已复制 4 池全部');
            }}
            className="btn-primary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-3 sm:px-5 py-1.5 sm:py-2.5"
          >
            <Sparkles size={12} /> 复制全部
          </button>
        </div>
      </div>

      {/* User kills */}
      <div className="card p-4 sm:p-5">
        <div className="text-sm font-semibold mb-2 sm:mb-3 flex items-center gap-2">
          <Check size={14} className="text-brand-rose" />
          手动杀号
        </div>
        <div className="flex gap-1.5 sm:gap-2 flex-wrap">
          {Array.from({ length: 10 }, (_, i) => i).map(n => (
            <button
              key={n}
              onClick={() => toggleKill(n)}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg font-mono font-semibold text-sm cursor-pointer transition-all ${
                userKills.includes(n)
                  ? 'bg-accent-danger/30 border border-accent-danger/60 text-accent-danger line-through'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
              }`}
            >
              {n}
            </button>
          ))}
          {userKills.length > 0 && (
            <button
              onClick={() => setUserKills([])}
              className="btn-ghost text-xs px-2 py-1"
            >
              清空
            </button>
          )}
        </div>
        <div className="text-[10px] sm:text-xs text-ink-3 mt-2 flex items-center gap-2 flex-wrap">
          <span>
            已选 <span className="text-accent-danger font-mono font-bold">{userKills.length}</span> / 10 个杀号
            {userKills.length > 0 && <span className="text-ink-2">· {userKills.sort((a,b)=>a-b).join(', ')}</span>}
          </span>
          {userKills.length > 0 && (
            <span className="text-brand-cyan">· 大底池已实时过滤(生效中)</span>
          )}
        </div>
      </div>

      {/* 5 折 CV 回测报告 + 加权共识杀号信号卡 — MAX v5 */}
      <div className="card p-3 sm:p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <FlaskConical size={14} className="text-brand-purple" />
              5 折 CV 回测报告(过去 30 期)
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-purple/15 text-brand-purple font-normal">MAX v5</span>
            </div>
            <p className="text-[10px] text-ink-3 mt-0.5">
              用过去 30 期真实数据,模拟 L1-L4 过滤流程,看每层池子能不能包含真值
            </p>
          </div>
          {backtestLoading && (
            <div className="text-[10px] text-ink-3 flex items-center gap-1">
              <Layers size={10} className="animate-pulse" /> 跑回测中...
            </div>
          )}
        </div>

        {backtest && backtest.sample > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { key: 'L1', label: 'L1 1000 注', expected: 100, color: 'text-ink-1',  bg: 'bg-ink-1/10' },
                { key: 'L2', label: 'L2 500 注',  expected: 50,  color: 'text-brand-cyan', bg: 'bg-brand-cyan/10' },
                { key: 'L3', label: 'L3 300 注',  expected: 30,  color: 'text-brand-purple', bg: 'bg-brand-purple/10' },
                { key: 'L4', label: 'L4 100 注',  expected: 10,  color: 'text-brand-gold',  bg: 'bg-brand-gold/10' },
              ].map(({ key, label, expected, color, bg }) => {
                const hit = backtest[key];
                const lift = hit - expected;  // 提升 = 命中率 - 理论(随机)
                return (
                  <div key={key} className={clsx('p-2 rounded-lg border border-white/10', bg)}>
                    <div className="text-[10px] text-ink-2">{label}</div>
                    <div className={clsx('text-xl font-bold', color)}>{hit.toFixed(1)}%</div>
                    <div className="text-[9px] text-ink-3 flex items-center gap-1">
                      <TrendingUp size={9} className={lift > 0 ? 'text-accent-success' : 'text-accent-danger'} />
                      <span className={lift > 0 ? 'text-accent-success' : 'text-accent-danger'}>
                        {lift > 0 ? '+' : ''}{lift.toFixed(1)}%
                      </span>
                      <span className="text-ink-3">vs 随机</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* 0-9 加权共识杀号信号展示 */}
            {smartKill && smartKill.scores && (
              <div className="p-2 rounded-lg bg-white/5 border border-white/10">
                <div className="text-[10px] text-brand-purple font-semibold mb-2 flex items-center gap-1">
                  <Sparkles size={10} /> 加权共识杀号(8 类信号 × 0-9 数字)
                </div>
                <div className="grid grid-cols-10 gap-1">
                  {smartKill.scores.map((score, code) => {
                    const isStrong = smartKill.smartKill.has(code);
                    const isWeak = smartKill.weakKill.has(code);
                    const reasons = (smartKill.reasons.get(code) || []).slice(0, 3);
                    return (
                      <div key={code} className="text-center" title={reasons.join(' / ')}>
                        <div className={clsx(
                          'w-7 h-7 mx-auto rounded-full flex items-center justify-center text-[11px] font-mono font-bold',
                          isStrong ? 'bg-accent-danger/20 text-accent-danger border border-accent-danger'
                            : isWeak ? 'bg-amber-500/20 text-amber-300 border border-amber-500'
                              : score > 0 ? 'bg-white/10 text-ink-1'
                                : 'bg-white/5 text-ink-3'
                        )}>
                          {code}
                        </div>
                        <div className="text-[8px] text-ink-3 mt-0.5">{score.toFixed(1)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-[9px] text-ink-3 leading-relaxed">
                  <span className="inline-block w-2 h-2 rounded-full bg-accent-danger mr-1 align-middle" />
                  红色 = 强杀号(≥ 1.0 分) L1 已过滤 ·
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mx-1 align-middle" />
                  橙色 = 弱杀号(0.5-1.0) L2 过滤 ·
                  <span className="inline-block w-2 h-2 rounded-full bg-white/20 mx-1 align-middle" />
                  灰 = 低权重
                </div>
              </div>
            )}
            <div className="mt-2 text-[9px] text-ink-3 leading-relaxed">
              💡 命中率 = 真实开奖号码在池子里的比例; 提升 = 命中率 - 随机命中率(L1=100%/1000=0.1%,L2=0.2%,L3=0.33%,L4=1%);
              5 折 CV 含义:用过去 30 期做 5 折时序验证,避免过拟合单期。
            </div>
          </>
        ) : (
          <div className="text-[10px] text-ink-3 text-center py-2">
            暂无回测数据(需至少 30 期历史)
          </div>
        )}
      </div>

      {/* 4 pools grid */}
      <PoolsView pools={pools} />

      {/* Footer info */}
      <div className="card p-3 sm:p-5 text-[10px] sm:text-xs text-ink-3 space-y-0.5 sm:space-y-1">
        <div>📊 数据源:500.com + 17500.cn 抓取 · 近 200 期真实历史</div>
        <div>🔬 回测基准:200 期真实开奖命中率,所有公式已标注准确率</div>
        <div>⚠️ 投注有风险,本系统仅供研究参考</div>
      </div>
    </div>
  );
}
